import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardShell from "@/components/DashboardShell";
import LockedGate from "@/components/LockedGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Users, Plus, Heart, Trash2, Loader2, Handshake, Search, Sparkles } from "lucide-react";

const ALL_PLATFORMS = [
  { id: "tiktok", name: "TikTok" }, { id: "instagram", name: "Instagram" },
  { id: "youtube", name: "YouTube" }, { id: "facebook", name: "Facebook" },
];
const TYPES = [
  { id: "shoutout", name: "Shoutout swap" }, { id: "duet", name: "Duet / collab video" },
  { id: "guest", name: "Guest feature" }, { id: "series", name: "Ongoing series" }, { id: "other", name: "Other" },
];
const compact = (n) => Intl.NumberFormat("en", { notation: "compact" }).format(n || 0);

const CollabCard = ({ c, onInterest, onDelete, busy }) => (
  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
    className="rounded-xl border border-border/50 bg-card p-5 space-y-3" data-testid={`collab-card-${c.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center text-primary font-bold text-sm shrink-0">
          {c.author_picture ? <img src={c.author_picture} alt="" className="w-9 h-9 rounded-full object-cover" /> : (c.author_name?.[0] || "C").toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate">{c.author_name}</div>
          <div className="text-xs text-muted-foreground">{c.platform_name} · {compact(c.followers)} followers</div>
        </div>
      </div>
      <Badge className="bg-primary/10 text-primary border border-primary/20 capitalize shrink-0">{c.collab_type}</Badge>
    </div>
    <div>
      <div className="font-heading font-semibold">{c.title}</div>
      <div className="text-sm text-muted-foreground mt-1">{c.description}</div>
    </div>
    <div className="flex flex-wrap gap-1.5">
      <Badge variant="outline" className="border-white/15">{c.niche}</Badge>
      {c.looking_for && <Badge variant="outline" className="border-white/15">Wants: {c.looking_for}</Badge>}
    </div>
    <div className="flex items-center justify-between pt-1">
      <div className="text-xs text-muted-foreground">{c.interest_count} interested</div>
      {c.is_mine ? (
        <Button variant="outline" size="sm" onClick={() => onDelete(c.id)} className="rounded-full border-white/15 text-secondary" data-testid={`collab-delete-${c.id}`}>
          <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
        </Button>
      ) : c.i_am_interested ? (
        <div className="text-xs text-primary font-medium flex items-center gap-1"><Handshake className="w-3.5 h-3.5" /> Reach out: {c.contact}</div>
      ) : (
        <Button size="sm" onClick={() => onInterest(c.id)} disabled={busy === c.id} className="rounded-full bg-primary text-background font-bold" data-testid={`collab-interest-${c.id}`}>
          {busy === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Heart className="w-3.5 h-3.5 mr-1" /> I'm in</>}
        </Button>
      )}
    </div>
  </motion.div>
);

const CollabBoard = () => {
  const { user } = useAuth();
  const locked = user && user.role !== "admin" && user.subscription_status !== "active";
  const [items, setItems] = useState([]);
  const [matches, setMatches] = useState([]);
  const [mine, setMine] = useState({ mine: [], interested: [] });
  const [fPlatform, setFPlatform] = useState("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState(false);
  const defaultPf = user?.platforms?.[0]?.platform || "tiktok";
  const [form, setForm] = useState({ platform: defaultPf, niche: "", collab_type: "shoutout", title: "", description: "", looking_for: "", followers: "", contact: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (locked) return;
    try {
      const params = new URLSearchParams();
      if (fPlatform !== "all") params.set("platform", fPlatform);
      if (q.trim()) params.set("q", q.trim());
      const [list, m] = await Promise.all([
        api.get(`/collab?${params.toString()}`),
        api.get("/collab/mine"),
      ]);
      setItems(list.data.items);
      setMine(m.data);
      try { const mm = await api.get("/collab/matches"); setMatches(mm.data.items); } catch { /* ignore */ }
    } catch { /* ignore */ }
  }, [fPlatform, q, locked]);

  useEffect(() => { load(); }, [load]);

  const interest = async (id) => {
    setBusy(id);
    try { await api.post(`/collab/${id}/interest`); toast.success("You're in! Their contact is now visible."); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not express interest"); }
    finally { setBusy(""); }
  };
  const remove = async (id) => {
    try { await api.delete(`/collab/${id}`); toast.success("Removed"); await load(); }
    catch { toast.error("Could not remove"); }
  };
  const submit = async () => {
    if (!form.title.trim() || !form.description.trim() || !form.contact.trim() || !form.niche.trim()) { toast.error("Fill in title, niche, description and contact"); return; }
    setSaving(true);
    try {
      await api.post("/collab", { ...form, followers: parseInt(form.followers || "0", 10) || 0 });
      toast.success("Posted to the board!");
      setOpen(false);
      setForm({ platform: defaultPf, niche: "", collab_type: "shoutout", title: "", description: "", looking_for: "", followers: "", contact: "" });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not post"); }
    finally { setSaving(false); }
  };

  if (locked) {
    return <LockedGate testid="collab-locked" title="Unlock the Collab Board"
      blurb="Post shoutout & collab requests, find creators in your niche, and grow together through cross-promotion." />;
  }

  return (
    <DashboardShell>
      <div className="p-6 md:p-10 max-w-5xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center"><Users className="w-5 h-5" /></div>
            <h1 className="font-heading text-3xl font-black tracking-tight">Collab Board</h1>
          </div>
          <Button onClick={() => setOpen(true)} className="rounded-full bg-primary text-background font-bold" data-testid="collab-new-btn"><Plus className="w-4 h-4 mr-1" /> Post a collab</Button>
        </div>
        <p className="text-muted-foreground mb-8">Find creators to cross-promote with. Post what you're looking for or jump into someone else's request.</p>

        <Tabs defaultValue="matches">
          <TabsList className="bg-card border border-border/50">
            <TabsTrigger value="matches" data-testid="collab-tab-matches"><Sparkles className="w-3.5 h-3.5 mr-1" /> Best matches</TabsTrigger>
            <TabsTrigger value="browse" data-testid="collab-tab-browse">Browse ({items.length})</TabsTrigger>
            <TabsTrigger value="mine" data-testid="collab-tab-mine">My board</TabsTrigger>
          </TabsList>

          <TabsContent value="matches" className="mt-6 space-y-4">
            <p className="text-sm text-muted-foreground">Ranked for you by niche, platform and audience size.</p>
            {matches.length === 0 ? (
              <div className="rounded-xl border border-dashed border-white/15 p-12 text-center text-muted-foreground" data-testid="collab-matches-empty">
                No matches yet. Set your niche in onboarding, or check back as more creators post.
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4" data-testid="collab-matches-list">
                {matches.map((c) => (
                  <div key={c.id} className="space-y-2" data-testid={`collab-match-${c.id}`}>
                    <div className="flex items-center gap-2 flex-wrap px-1">
                      <Badge className="bg-primary text-background border-0 font-bold" data-testid={`collab-match-score-${c.id}`}>{c.match_score}% match</Badge>
                      {(c.match_reasons || []).map((r, i) => <span key={i} className="text-xs text-muted-foreground">· {r}</span>)}
                    </div>
                    <CollabCard c={c} onInterest={interest} onDelete={remove} busy={busy} />
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="browse" className="mt-6 space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <Select value={fPlatform} onValueChange={setFPlatform}>
                <SelectTrigger className="h-11 bg-transparent border-white/10 w-full sm:w-44" data-testid="collab-filter-platform"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All platforms</SelectItem>{ALL_PLATFORMS.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by niche or keyword" className="h-11 bg-transparent border-white/10 pl-9" data-testid="collab-search" />
              </div>
            </div>
            {items.length === 0 ? (
              <div className="rounded-xl border border-dashed border-white/15 p-12 text-center text-muted-foreground" data-testid="collab-empty">No open collab posts yet. Be the first to post one!</div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">{items.map((c) => <CollabCard key={c.id} c={c} onInterest={interest} onDelete={remove} busy={busy} />)}</div>
            )}
          </TabsContent>

          <TabsContent value="mine" className="mt-6 space-y-6">
            <div>
              <div className="text-sm font-semibold mb-3">Your posts</div>
              {mine.mine.length === 0 ? <div className="text-sm text-muted-foreground">You haven't posted anything yet.</div>
                : <div className="grid md:grid-cols-2 gap-4">{mine.mine.map((c) => <CollabCard key={c.id} c={c} onInterest={interest} onDelete={remove} busy={busy} />)}</div>}
            </div>
            <div>
              <div className="text-sm font-semibold mb-3">Collabs you're in</div>
              {mine.interested.length === 0 ? <div className="text-sm text-muted-foreground">You haven't joined any collabs yet.</div>
                : <div className="grid md:grid-cols-2 gap-4">{mine.interested.map((c) => <CollabCard key={c.id} c={c} onInterest={interest} onDelete={remove} busy={busy} />)}</div>}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border/50" data-testid="collab-dialog">
          <DialogHeader><DialogTitle>Post a collab request</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Platform</Label>
                <Select value={form.platform} onValueChange={(v) => setForm((f) => ({ ...f, platform: v }))}>
                  <SelectTrigger className="h-11 bg-transparent border-white/10 mt-1" data-testid="collab-form-platform"><SelectValue /></SelectTrigger>
                  <SelectContent>{ALL_PLATFORMS.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Type</Label>
                <Select value={form.collab_type} onValueChange={(v) => setForm((f) => ({ ...f, collab_type: v }))}>
                  <SelectTrigger className="h-11 bg-transparent border-white/10 mt-1" data-testid="collab-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Niche</Label><Input value={form.niche} onChange={(e) => setForm((f) => ({ ...f, niche: e.target.value }))} placeholder="e.g. fitness" className="h-11 bg-transparent border-white/10 mt-1" data-testid="collab-form-niche" /></div>
              <div><Label className="text-xs">Your followers</Label><Input type="number" value={form.followers} onChange={(e) => setForm((f) => ({ ...f, followers: e.target.value }))} placeholder="12000" className="h-11 bg-transparent border-white/10 mt-1" data-testid="collab-form-followers" /></div>
            </div>
            <div><Label className="text-xs">Title</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Looking for duet partners in fitness" className="h-11 bg-transparent border-white/10 mt-1" data-testid="collab-form-title" /></div>
            <div><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="What do you want to do, and who's the ideal partner?" className="bg-transparent border-white/10 mt-1 min-h-[80px]" data-testid="collab-form-description" /></div>
            <div><Label className="text-xs">Looking for (optional)</Label><Input value={form.looking_for} onChange={(e) => setForm((f) => ({ ...f, looking_for: e.target.value }))} placeholder="e.g. 10k+ fitness creators" className="h-11 bg-transparent border-white/10 mt-1" data-testid="collab-form-lookingfor" /></div>
            <div><Label className="text-xs">Contact (shown to interested creators)</Label><Input value={form.contact} onChange={(e) => setForm((f) => ({ ...f, contact: e.target.value }))} placeholder="DM @yourhandle or email" className="h-11 bg-transparent border-white/10 mt-1" data-testid="collab-form-contact" /></div>
          </div>
          <DialogFooter>
            <Button onClick={submit} disabled={saving} className="rounded-full bg-primary text-background font-bold w-full" data-testid="collab-form-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Post to board"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
};

export default CollabBoard;
